import cn from 'classnames'
import React, { memo, useCallback, useEffect, useMemo } from 'react'

import { Content } from './components/content/Content'
import { Menu } from './components/menu/Menu'
import { MenuItem } from './components/menuItem/MenuItem'
import { MenuSeparator } from './components/menuSeparator/MenuSeparator'
import { MenuSharedBlock } from './components/menuSharedBlock/MenuSharedBlock'
import { isMenuItem, isMenuSeparator, isMenuSharedItem } from './helpers'
import { ISideMenuGroup, ISideMenuItemEntry, ISideMenuProps, TSideMenuItemId } from './models'

import styles from './SideMenu.module.scss'

export const SideMenu = memo<ISideMenuProps>(
  ({
    children,
    classes,
    keepMounted,
    onItemSelect,
    onItemSelectById,
    color = 'primary',
    selectedItem = 0,
    selectedItemId,
    variant = 'text',
  }) => {
    const { displayItems, groups, itemById, itemByOrder, sharedItems } = useMemo(() => {
      const nextItems: ISideMenuItemEntry[] = []
      const nextSharedItems: React.ReactNode[] = []
      let groupIndex = 0

      React.Children.forEach(children, (child) => {
        if (isMenuSharedItem(child)) {
          nextSharedItems.push(child)
          return
        }

        if (isMenuItem(child)) {
          const logicalIndex = nextItems.length
          const item: ISideMenuItemEntry = {
            id: child.props.id ?? logicalIndex,
            hidden: !!child.props.isHidden,
            logicalIndex,
            order: child.props.order ?? logicalIndex,
            groupIndex,
            child,
          }

          nextItems.push(item)

          return
        }

        if (isMenuSeparator(child)) {
          groupIndex += 1
        }
      })

      const groupedItems = nextItems.reduce<Map<number, ISideMenuItemEntry[]>>((acc, item) => {
        const group = acc.get(item.groupIndex) ?? []
        group.push(item)
        acc.set(item.groupIndex, group)
        return acc
      }, new Map())

      const nextGroups: ISideMenuGroup[] = Array.from(groupedItems.entries())
        .sort(([leftGroup], [rightGroup]) => leftGroup - rightGroup)
        .map(([currentGroupIndex, items]) => ({
          key: `group-${currentGroupIndex}`,
          items: [...items].sort((leftItem, rightItem) => {
            if (leftItem.order !== rightItem.order) {
              return leftItem.order - rightItem.order
            }

            return leftItem.logicalIndex - rightItem.logicalIndex
          }),
        }))

      const nextDisplayItems = nextGroups.flatMap((group) => group.items)
      const nextItemById = nextDisplayItems.reduce<Map<TSideMenuItemId, ISideMenuItemEntry>>(
        (acc, item) => acc.set(item.id, item),
        new Map()
      )
      const nextItemByOrder = nextDisplayItems.reduce<Map<number, ISideMenuItemEntry>>(
        (acc, item) => {
          if (!acc.has(item.order)) {
            acc.set(item.order, item)
          }

          return acc
        },
        new Map()
      )

      return {
        displayItems: nextDisplayItems,
        groups: nextGroups,
        itemById: nextItemById,
        itemByOrder: nextItemByOrder,
        sharedItems: nextSharedItems,
      }
    }, [children])

    const visibleItems = useMemo(() => displayItems.filter((item) => !item.hidden), [displayItems])
    const isIdMode = selectedItemId != null

    const requestedItem = useMemo(() => {
      if (isIdMode) {
        return itemById.get(selectedItemId) ?? null
      }

      return itemByOrder.get(selectedItem) ?? null
    }, [isIdMode, itemById, itemByOrder, selectedItem, selectedItemId])

    const resolvedItem = useMemo(() => {
      if (requestedItem && !requestedItem.hidden) {
        return requestedItem
      }

      return visibleItems[0] ?? null
    }, [requestedItem, visibleItems])

    const [activeItemId, setActiveItemId] = React.useState<TSideMenuItemId | null>(
      resolvedItem?.id ?? null
    )

    const emitSelection = useCallback(
      (item: ISideMenuItemEntry) => {
        onItemSelectById?.(item.id)
        onItemSelect?.(item.order)
      },
      [onItemSelect, onItemSelectById]
    )

    const handleMenuItemClick = useCallback(
      (item: ISideMenuItemEntry) => {
        setActiveItemId(item.id)
        emitSelection(item)
      },
      [emitSelection]
    )

    useEffect(() => {
      setActiveItemId(resolvedItem?.id ?? null)
    }, [resolvedItem?.id])

    useEffect(() => {
      if (!resolvedItem) {
        return
      }

      if (requestedItem?.id === resolvedItem.id && !requestedItem.hidden) {
        return
      }

      emitSelection(resolvedItem)
    }, [emitSelection, requestedItem?.hidden, requestedItem?.id, resolvedItem])

    return (
      <div className={cn(styles.container, classes?.container)}>
        <Menu
          activeItemId={activeItemId}
          onMenuItemClick={handleMenuItemClick}
          groups={groups}
          classes={classes}
          color={color}
          variant={variant}
        />
        <Content
          activeItemId={activeItemId}
          keepMounted={keepMounted}
          items={displayItems}
          sharedItems={sharedItems}
          classes={classes}
        />
      </div>
    )
  }
) as React.NamedExoticComponent<ISideMenuProps> & {
  Item: typeof MenuItem
  Separator: typeof MenuSeparator
  SharedBlock: typeof MenuSharedBlock
}

SideMenu.Item = MenuItem
SideMenu.Separator = MenuSeparator
SideMenu.SharedBlock = MenuSharedBlock

SideMenu.displayName = 'SideMenu'
